import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppState, useDispatch } from '../state/AppContext';
import { ADD_SEED_ARTIST, START_GENERATING, SET_GALAXY_DATA, SET_LOADING_PROGRESS, SET_ERROR } from '../state/actions';
import { findArtistByName } from '../api/deezerClient';
import { getSimilarArtists } from '../api/lastfmClient';
import { generateRecommendations } from '../engine/recommendationEngine';
import { unslugifyArtist } from '../utils/exportUtils';
import LoadingAnimation from './LoadingAnimation';
import GalaxyView from './GalaxyView';
import '../styles/similarTo.css';

function SimilarToPage({ slug }) {
  const { phase } = useAppState();
  const dispatch = useDispatch();
  const [setupError, setSetupError] = useState(null);
  const [initiated, setInitiated] = useState(false);

  const displayName = unslugifyArtist(slug);

  const initGalaxy = useCallback(async () => {
    if (initiated) return;
    setInitiated(true);

    try {
      // Get top 2 similar artists from Last.fm
      const similar = await getSimilarArtists(displayName, 5);
      const topTwoNames = similar.slice(0, 2).map(a => a.name);

      if (topTwoNames.length < 2) {
        setSetupError('Could not find enough similar artists. Try exploring on Stellar directly.');
        return;
      }

      // Resolve all 3 artists on Deezer (primary + 2 similar)
      const allNames = [displayName, ...topTwoNames];
      const deezerResults = await Promise.all(
        allNames.map(name => findArtistByName(name, { priority: true }))
      );

      // Filter out any that didn't resolve
      const seeds = deezerResults.filter(Boolean);
      if (seeds.length < 3) {
        setSetupError('Could not resolve all seed artists. Try exploring on Stellar directly.');
        return;
      }

      // Add seeds to app state
      for (const seed of seeds) {
        dispatch({ type: ADD_SEED_ARTIST, payload: seed });
      }

      // Trigger galaxy generation
      dispatch({ type: START_GENERATING });

      const galaxyData = await generateRecommendations(
        seeds,
        (progress) => {
          dispatch({ type: SET_LOADING_PROGRESS, payload: progress });
        }
      );
      dispatch({ type: SET_GALAXY_DATA, payload: galaxyData });
    } catch (err) {
      console.error('SimilarToPage generation failed:', err);
      setSetupError(err.message || 'Failed to generate galaxy. Please try again.');
      dispatch({
        type: SET_ERROR,
        payload: err.message || 'Failed to generate recommendations.',
      });
    }
  }, [displayName, dispatch, initiated]);

  useEffect(() => {
    initGalaxy();
  }, [initGalaxy]);

  // This SEO route only knows how to render the generated galaxy. Once the
  // map has been shown, hitting the galaxy's Back button resets the phase to
  // 'inputting' — which this route can't render, so it would otherwise strand
  // on the initial "Discovering…" spinner. Send the user to the main app home.
  const reachedGalaxy = useRef(false);
  useEffect(() => {
    if (phase === 'viewing' || phase === 'loading') {
      reachedGalaxy.current = true;
    } else if (reachedGalaxy.current && phase === 'inputting') {
      window.location.assign('/');
    }
  }, [phase]);

  // Once galaxy is generated, show full GalaxyView
  if (phase === 'viewing') {
    return <GalaxyView />;
  }

  // Loading / generating state
  if (phase === 'loading') {
    return <LoadingAnimation />;
  }

  // Initial setup or error state
  return (
    <div className="similar-to-page">
      <header className="similar-to-header">
        <a href="/" className="share-logo">
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
          </svg>
          Stellar
        </a>
      </header>

      <main className="similar-to-content">
        {setupError ? (
          <div className="similar-to-error">
            <h1>Artists Like {displayName}</h1>
            <p>{setupError}</p>
            <a href="/" className="share-cta-btn">Explore on Stellar</a>
          </div>
        ) : (
          <div className="similar-to-loading">
            <div className="share-loading-spinner" />
            <span>Discovering artists like {displayName}...</span>
          </div>
        )}
      </main>
    </div>
  );
}

export default SimilarToPage;
